import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useDeleteIngredientMutation,
  useGetIngredientQuery,
} from "@/redux/services/inventory/ingredient.api";
import { useGetStockAdjustmentsQuery } from "@/redux/services/inventory/stockAdjustment.api";
import {
  Ingredient,
  StockAdjustment,
} from "@/redux/services/inventory/inventory.type";
import { displayPrice, formatStock } from "@/lib/utils";
import { useRestaurantCurrency } from "@/lib/hooks";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";
import { PlusCircle, Pencil, Trash2 } from "lucide-react";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { usePermissions } from "@/hooks/usePermissions";
import toast from "react-hot-toast";
import IngredientFormModal from "./IngredientFormModal";
import StockAdjustmentModal from "./StockAdjustmentModal";
import PackagingsSection from "./packagings/PackagingsSection";
import AdjustmentReasonBadge from "@/components/inventory/adjustments/AdjustmentReasonBadge";
import OrderDetailsDrawer from "@/components/order/OrderDetailsDrawer";

interface IngredientDetailsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  restaurantId: string;
  ingredientId: string | null;
}

const IngredientDetailsDrawer = ({
  isOpen,
  onClose,
  restaurantId,
  ingredientId,
}: IngredientDetailsDrawerProps) => {
  const currency = useRestaurantCurrency();
  const { can, canManage } = usePermissions();
  // Purchase orders need suppliers.view (SFRESTO-165).
  const canViewPurchaseOrders = can("suppliers.view");
  const canManageInventory = canManage("inventory");
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isAdjustOpen, setIsAdjustOpen] = useState(false);
  const [orderDrawerId, setOrderDrawerId] = useState<string | null>(null);

  const { data: ingredient, isLoading } = useGetIngredientQuery(
    {
      restaurantId,
      ingredientUuid: ingredientId ?? "",
    },
    { skip: !ingredientId }
  );

  const { data: historyData, isFetching: isFetchingHistory } =
    useGetStockAdjustmentsQuery(
      {
        query: { ingredientUuid: ingredientId ?? undefined, page: 1 },
      },
      { skip: !ingredientId }
    );

  const [deleteIngredient] = useDeleteIngredientMutation();
  const confirm = useConfirm();

  const handleDelete = async (ing: Ingredient) => {
    const ok = await confirm({
      title: `Supprimer "${ing.name}" ?`,
      description: "Cette action est irréversible.",
      confirmText: "Supprimer",
      cancelText: "Annuler",
      variant: "destructive",
      icon: <Trash2 className="h-6 w-6" />,
    });
    if (!ok) return;
    const res = await deleteIngredient({
      restaurantId,
      ingredientUuid: ing.id,
    });
    if ("error" in res) {
      toast.error("Erreur lors de la suppression");
      return;
    }
    toast.success("Ingrédient supprimé avec succès");
    onClose();
  };

  const formatQuantity = (quantity: string, unit: string) => {
    const parsed = parseFloat(quantity);
    const signClass =
      parsed < 0 ? "text-red-600" : parsed > 0 ? "text-green-600" : "";
    const prefix = parsed > 0 ? "+" : "";
    return (
      <span className={`${signClass} font-medium`}>
        {prefix}
        {formatStock(quantity, unit)}
      </span>
    );
  };

  const renderReference = (adj: StockAdjustment) => {
    if (adj.order) {
      const order = adj.order;
      return (
        <button
          type="button"
          onClick={() => setOrderDrawerId(order.id)}
          className="text-card-foreground hover:text-blue-700 hover:underline"
        >
          {order.orderNumber}
        </button>
      );
    }
    if (adj.purchaseOrder) {
      if (!canViewPurchaseOrders) {
        return (
          <span className="text-card-foreground">
            {adj.purchaseOrder.poNumber}
          </span>
        );
      }
      return (
        <Link
          to={`/inventory/purchase-orders/${adj.purchaseOrder.id}`}
          className="text-card-foreground hover:text-blue-700 hover:underline"
          onClick={onClose}
        >
          {adj.purchaseOrder.poNumber}
        </Link>
      );
    }
    if (adj.stockCount) {
      return (
        <Link
          to={`/inventory/stock-counts/${adj.stockCount.id}`}
          className="text-card-foreground hover:text-blue-700 hover:underline"
          onClick={onClose}
        >
          Inventaire
        </Link>
      );
    }
    return null;
  };

  const adjustments = historyData?.results ?? [];

  return (
    <>
      <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-md p-0 overflow-y-auto"
        >
          <div className="p-6 space-y-6">
            {isLoading || !ingredient ? (
              <div className="space-y-4">
                <Skeleton className="w-3/4 h-6" />
                <Skeleton className="w-full h-24" />
                <Skeleton className="w-full h-40" />
              </div>
            ) : (
              <>
                <SheetHeader>
                  <div className="flex items-center gap-2 flex-wrap">
                    <SheetTitle>{ingredient.name}</SheetTitle>
                    {ingredient.lowStock && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                        Stock faible
                      </span>
                    )}
                  </div>
                  <SheetDescription>
                    Unité : {ingredient.baseUnit} · Seuil :{" "}
                    {formatStock(
                      ingredient.lowStockThreshold,
                      ingredient.baseUnit
                    )}
                  </SheetDescription>
                </SheetHeader>

                <div className="grid grid-cols-3 gap-3">
                  <div className="dash__card p-3">
                    <p className="text-xs text-muted-foreground">Disponible</p>
                    <p className="text-base font-semibold text-card-foreground">
                      {formatStock(ingredient.availableStock, ingredient.baseUnit)}
                    </p>
                  </div>
                  <div className="dash__card p-3">
                    <p className="text-xs text-muted-foreground">Réservé</p>
                    <p className="text-base font-semibold text-card-foreground">
                      {formatStock(ingredient.reservedStock, ingredient.baseUnit)}
                    </p>
                  </div>
                  <div className="dash__card p-3">
                    <p className="text-xs text-muted-foreground">Total</p>
                    <p className="text-base font-semibold text-card-foreground">
                      {formatStock(ingredient.currentStock, ingredient.baseUnit)}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="dash__card p-3">
                    <p className="text-xs text-muted-foreground">Coût unitaire</p>
                    <p className="text-base font-semibold text-card-foreground">
                      {ingredient.costPerUnit > 0
                        ? displayPrice(ingredient.costPerUnit, currency)
                        : "—"}
                    </p>
                    {ingredient.costUpdatedAt && (
                      <p className="text-[10px] text-muted-foreground mt-0.5">
                        maj {format(parseISO(ingredient.costUpdatedAt), "d MMM yyyy", {
                          locale: fr,
                        })}
                      </p>
                    )}
                  </div>
                  <div className="dash__card p-3">
                    <p className="text-xs text-muted-foreground">Valeur en stock</p>
                    <p className="text-base font-semibold text-card-foreground">
                      {ingredient.inventoryValue > 0
                        ? displayPrice(ingredient.inventoryValue, currency)
                        : "—"}
                    </p>
                  </div>
                </div>

                {canManageInventory && (
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => setIsAdjustOpen(true)}
                    >
                      <PlusCircle className="h-4 w-4" />
                      Ajuster le stock
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => setIsEditOpen(true)}
                    >
                      <Pencil className="h-4 w-4" />
                      Modifier
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="destructive"
                      onClick={() => handleDelete(ingredient)}
                    >
                      <Trash2 className="h-4 w-4" />
                      Supprimer
                    </Button>
                  </div>
                )}

                <PackagingsSection ingredient={ingredient} />

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-semibold text-card-foreground">
                      Derniers mouvements
                    </h3>
                    <Link
                      to={`/inventory/adjustments`}
                      className="text-xs text-blue-600 hover:text-blue-700 hover:underline"
                      onClick={onClose}
                    >
                      Voir tout
                    </Link>
                  </div>
                  {isFetchingHistory ? (
                    <div className="space-y-2">
                      {Array.from({ length: 3 }).map((_, i) => (
                        <Skeleton key={i} className="w-full h-10" />
                      ))}
                    </div>
                  ) : adjustments.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-6">
                      Aucun mouvement enregistré
                    </p>
                  ) : (
                    <ul className="space-y-2">
                      {adjustments.slice(0, 10).map((adj) => {
                        const ref = renderReference(adj);
                        return (
                          <li
                            key={adj.id}
                            className="bg-muted/40 rounded-md px-3 py-2 text-sm"
                          >
                            <div className="flex items-center justify-between gap-2 flex-wrap">
                              <div className="flex items-center gap-2 flex-wrap">
                                <AdjustmentReasonBadge reason={adj.reason} />
                                {formatQuantity(adj.quantity, ingredient.baseUnit)}
                              </div>
                              {ref && (
                                <span className="text-xs">Réf : {ref}</span>
                              )}
                            </div>
                            <div className="flex items-center justify-between gap-2 mt-1 text-xs text-muted-foreground">
                              <span>
                                {format(
                                  parseISO(adj.createdAt),
                                  "d MMM yyyy, HH:mm",
                                  { locale: fr }
                                )}
                              </span>
                              <span>
                                {adj.adjustedBy?.fullName ?? "Système"}
                              </span>
                            </div>
                            {adj.notes && (
                              <p className="text-xs text-muted-foreground mt-1 italic">
                                {adj.notes}
                              </p>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {ingredient && isEditOpen && (
        <IngredientFormModal
          isOpen={isEditOpen}
          onClose={() => setIsEditOpen(false)}
          ingredientToEdit={ingredient}
          restaurantId={restaurantId}
        />
      )}

      {ingredient && isAdjustOpen && (
        <StockAdjustmentModal
          isOpen={isAdjustOpen}
          onClose={() => setIsAdjustOpen(false)}
          ingredient={ingredient}
        />
      )}

      <OrderDetailsDrawer
        orderId={orderDrawerId ?? ""}
        isOpen={!!orderDrawerId}
        onClose={() => setOrderDrawerId(null)}
      />
    </>
  );
};

export default IngredientDetailsDrawer;
